import React, { useState, useEffect } from 'react';
import {
  paramsConfig,
  paramsConfig_SWC,
  paramsNames,
  pumpStatecolours,
  pumpStateStrings,
  applianceStatecolours,
} from '../params';
import { REGS } from '../registers';
import {
  ExtractString,
  getFaultTableItem,
  getUIDFromDb,
  getIDSFromDb,
  getShortIDFromUID,
  getRecordIDFromUID,
  getRecordIDFromUIDNoDT,
  getDateTimeFromRecordID,
  getMacAddressFromUID,
  getModelTypeValueFromUID,
  getMonthYearFromDate,
  speedHasElement,
  isCurrentFaultsARealFault,
  is_a_SWC,
} from '../components/Common';
import {
  compareAsc,
  differenceInDays,
  endOfDay,
  endOfToday,
  formatISO9075,
  lightFormat,
  startOfDay,
  startOfYesterday,
} from 'date-fns';
import { listDevices, getDevice, listInformation } from '../graphql/queries';
import { deleteDevice, updateDevice, createDevice } from '../graphql/mutations';
import { MdCheckCircleOutline, MdErrorOutline, MdSensorsOff } from 'react-icons/md';
import { getRandomIntInclusive } from '../utils/utilities';
import {
  APP_CONFIG,
  accessLevels,
  controller_t,
  datetimeFormats,
  faultTable,
  modelTypeDetails,
  modelTypeDetails_no_SWC,
  sortKeys,
  sortOrder,
  FIRMWARE_VERSIONS,
  RECORD_TYPES,
  apiLimit,
} from '../constants';
import { COLOURS } from '../styles';
import { canIaccessController } from '../components/AccessControl';
import { fetchUserAccessRecord } from '../components/UserAccess';
import { client, timer } from '../utils/apiClient';
import { devLog } from '../utils/devLog';

// devicesStats.createdMonth buckets store both fixed metadata (name/year/month)
// and dynamic per-model counters keyed by a device's own self-reported
// modelType, lowercased. modelType is external data, so a device reporting one
// of these reserved names (e.g. "Name") must never be allowed to overwrite the
// bucket's own metadata field.
const RESERVED_CREATED_MONTH_KEYS = ['name', 'year', 'month'];

export function useDevices({ loginId, userGroups, accessLevel, setAccessLevel }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const [progress, setProgress] = useState(0);
  const [logDevice, setLogDevice] = useState('');

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedFilterDateRange, setSelectedFilterDateRange] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState([startOfYesterday(), endOfToday()]);
  const [selectedParams, setSelectedParams] = useState([paramsConfig[0].text]);
  const [historyFaults, setHistoryFaults] = useState([]);
  const [notes_data, setNotesData] = useState([]);

  const [accessableGroups, setGroupAccess] = useState([]);
  const [accessableDevices, setAccessableDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedShortID, setShortID] = useState('');
  const [logData, setLogData] = useState([]);
  const [applianceTrackerDataArray, setApplianceTrackerData] = useState([]);
  const [pumpTrackerData, setPumpTrackerData] = useState([]);
  const [ORPPHLabelsData, setORPPHLabels] = useState([]);
  const [ORPData, setORPData] = useState([]);
  const [PHData, setPHData] = useState([]);
  const [ORPRTData, setORPRTData] = useState([]);
  const [PHRTData, setPHRTData] = useState([]);
  const [god_mode, setGodMode] = useState(false);

  const [installers, setInstallers] = useState([]);
  const [installedBy, setInstalledBy] = useState([]);

  const [selectedSortKey, setSelectedSortKey] = useState(sortKeys.name);
  const [selectedSortOrder, setSelectedSortOrder] = useState(sortOrder.ascending);
  const selectedSortKeyRef = React.useRef(selectedSortKey);
  const selectedSortOrderRef = React.useRef(selectedSortOrder);
  useEffect(() => {
    selectedSortKeyRef.current = selectedSortKey;
    selectedSortOrderRef.current = selectedSortOrder;
  }, [selectedSortKey, selectedSortOrder]);

  const [currentDevice, setCurrentDevice] = useState('');
  const [devicesStats, setDevicesStats] = useState({});
  const [configData, setConfigData] = useState([]);

  const [plottableParams, setPlottableParams] = useState(paramsConfig);
  const [plottableConfigParams, setPlottableConfigParams] = useState([]);

  // Mutated imperatively from inside fetchLogData() only, not through a
  // setter — a plain `let` here would reset to true on every useDevices()
  // call (i.e. every render), discarding whatever the previous fetchLogData()
  // run detected. Refs persist across renders so the mutation survives until
  // the next fetch overwrites it. Deliberately NOT touched by
  // fetchSetPointData(): viewDevice() kicks off fetchSetPointData() and
  // fetchLogData() concurrently with no ordering guarantee, so if
  // fetchSetPointData() also wrote here, whichever finished last would win —
  // a device with a configured PH_SP/ORP_SP setpoint but zero actual log
  // readings in range could have fetchSetPointData's later-arriving config
  // fetch silently flip these back to true, reintroducing the wrong
  // "No pH/ORP readings to display" behavior these refs exist to drive.
  // Which controller the current picker selection was made against. A ref rather than
  // state for the same reason phSelectionRef is one: it is mutated from inside
  // fetchLogData and has to survive renders.
  const selectionDeviceRef = React.useRef('');

  // Identifies the most recent fetchLogData *request*, so its tail can tell whether it is
  // still the newest before touching the selection. A monotonic counter rather than the
  // device id: two overlapping fetches for the same controller — rapid date-range changes —
  // share a device id, so an id could tell controllers apart but not requests, and an older
  // request landing last would recompute the selection from its own data and its own stale
  // closure over selectedParams.
  //
  // Never reset, deliberately. Only equality matters, and restarting the count could let a
  // new request collide with the token of one still in flight.
  const logFetchTokenRef = React.useRef(0);

  const phSelectionRef = React.useRef(true);
  const ORPSelectionRef = React.useRef(true);

  const [configuredAppliances, setConfiguredAppliance] = useState([]);

  const isApplianceConfigured = (id_to_check) => {
    var result = false;
    if (configuredAppliances.includes(id_to_check)) {
      result = true;
    } else {
      result = false;
    }
    return result;
  };

  const getAppliances_with_id = (appliance_array, id_to_check) => {
    // getTrackerdataForAppliances_with_id (the only caller) is itself exposed
    // from the hook, so appliance_array can be whatever an external caller
    // passes — guard the same way getPumpTrackerdata does, rather than
    // letting .filter throw on undefined/non-array input.
    if (!Array.isArray(appliance_array)) {
      return null;
    }

    let return_appliances = appliance_array.filter((appliance) => appliance.id === id_to_check);

    if (return_appliances.length !== 0) {
      return return_appliances;
    } else {
      return null;
    }
  };

  const pause = () => {
    return new Promise((r) => setTimeout(r, 0));
  };

  const getTrackerdataForAppliances_with_id = (appliance_array, id_to_check) => {
    let apppliance_data = getAppliances_with_id(appliance_array, id_to_check);
    var applianceTrackerBlocks = [];

    if (apppliance_data === null) {
      // console.log ( "No Data for app: ", id_to_check);
    } else {
      var trackerBlock = {};
      var offset = 0;

      apppliance_data.forEach((item) => {
        trackerBlock = {};
        trackerBlock.key = String(offset);
        trackerBlock.color = item.colour;
        trackerBlock.tooltip = item.DisplayString;
        trackerBlock.dateDisplay = item.dateDisplay;
        if (item.dateTime !== undefined) {
          trackerBlock.dateTime = item.dateTime;
        }
        trackerBlock.value = item.value;

        offset++;
        applianceTrackerBlocks.push(trackerBlock);
      });
      // console.log ( "Appliance items:", applianceTrackerBlocks);
    }
    return applianceTrackerBlocks;
  };
  const getPumpTrackerdata = (pump_array) => {
    //let apppliance_data = getAppliances_with_id( appliance_array, id_to_check)
    var pumpTrackerBlocks = [];

    if (Array.isArray(pump_array)) {
      var trackerBlock = {};
      var offset = 0;

      pump_array.forEach((item) => {
        trackerBlock = {};
        trackerBlock.key = String(offset);
        trackerBlock.color = item.colour;
        trackerBlock.tooltip = item.DisplayString;
        trackerBlock.dateDisplay = item.dateString;
        if (item.dateTime !== undefined) {
          trackerBlock.dateTime = item.dateTime;
        }
        trackerBlock.value = item.value;
        offset++;
        pumpTrackerBlocks.push(trackerBlock);
      });
      // console.log ( "Appliance items:", applianceTrackerBlocks);
    }
    return pumpTrackerBlocks;
  };

  const handleSelectedDevice = (device) => {
    setSelectedDevice(device);
    devLog('HSD');
    if (device !== '') {
      setShortID(getShortIDFromUID(device));
    } else {
      setShortID('');
    }
  };

  const refreshData = () => {
    if (selectedDevice === null || selectedDevice === undefined) {
      devLog('No controller defined ');
    } else if (selectedDevice.length === 0) {
      devLog('No controller selected');
    } else {
      fetchSetPointData(selectedDevice, selectedDateRange);
      fetchLogData(selectedDevice, selectedDateRange);
    }
  };

  const refreshFaultsData = () => {
    if (selectedDevice === null || selectedDevice === undefined) {
      devLog('No controller defined ');
    } else if (selectedDevice.length === 0) {
      devLog('No controller selected');
    } else {
      fetchFaultsData(selectedDevice, selectedDateRange);
    }
  };

  const is_loading_or_no_device = () => {
    var result = false;

    if (loading) {
      result = true;
    }
    if (loadingLogs) {
      result = true;
    }
    if (loadingConfig) {
      result = true;
    }

    if (result === false && (!selectedDevice || selectedDevice.length === 0)) {
      //   console.log("no device")
      result = true;
    }
    return result;
  };

  const refreshDataByDate = (range) => {
    setSelectedDateRange(range);
    if (selectedDevice.length === 0) {
      devLog('No controller selected');
    } else {
      fetchSetPointData(selectedDevice, range);
      fetchLogData(selectedDevice, range);
    }
  };

  const refreshFaultDataByDate = (range) => {
    setSelectedDateRange(range);
    devLog('refreshFaultDataByDate', range);

    if (selectedDevice.length === 0) {
      devLog('No controller selected');
    } else {
      fetchFaultsData(selectedDevice, range);
    }
  };

  const updateSelectedDevices = (value) => {
    setSelectedDevices(value);
    if (value.length === 0) {
      handleSelectedDevice('');
    } else if (value.length === 1) {
      handleSelectedDevice(value[0]);
    }
  };
  const sortDevicesArray = (allDevices, key, order) => {
    const tempDevices = [...allDevices];
    switch (key) {
      case sortKeys.uid:
        tempDevices.sort(
          (a, b) => (a.uniqueDeviceId < b.uniqueDeviceId ? -1 : 1) * (order === sortOrder.ascending ? 1 : -1),
        );
        break;
      case sortKeys.type:
        tempDevices.sort((a, b) => (a.modelType < b.modelType ? -1 : 1) * (order === sortOrder.ascending ? 1 : -1));
        break;
      case sortKeys.name:
        tempDevices.sort(
          (a, b) =>
            (a.userName.toLowerCase() < b.userName.toLowerCase() ? -1 : 1) * (order === sortOrder.ascending ? 1 : -1),
        );
        break;
      case sortKeys.status:
        tempDevices.sort((a, b) => (a.statusDT < b.statusDT ? -1 : 1) * (order === sortOrder.ascending ? 1 : -1));
        break;
      default:
        devLog('Invalid sort key:', key);
        return null;
    }
    return tempDevices;
  };

  const sortDevices = (allDevices, accLvl, key, order) => {
    //console.log("key:", key, "order:", order);
    let doUpdate = true;
    const tempDevices = [...allDevices];
    // if(accLvl === accessLevels.guest) {
    //   tempDevices.sort((a, b) => (a.guestName.toLowerCase() < b.guestName.toLowerCase() ? -1 : 1));
    // } else {
    switch (key) {
      case sortKeys.uid:
        if (order === sortOrder.ascending) {
          tempDevices.sort((a, b) => (a.uniqueDeviceId < b.uniqueDeviceId ? -1 : 1));
        } else {
          // descending
          tempDevices.sort((a, b) => (a.uniqueDeviceId < b.uniqueDeviceId ? 1 : -1));
        }
        break;
      case sortKeys.type:
        if (order === sortOrder.ascending) {
          tempDevices.sort((a, b) => (a.modelType < b.modelType ? -1 : 1));
        } else {
          // descending
          tempDevices.sort((a, b) => (a.modelType < b.modelType ? 1 : -1));
        }
        break;

      case sortKeys.name:
        if (order === sortOrder.ascending) {
          tempDevices.sort((a, b) => (a.userName.toLowerCase() < b.userName.toLowerCase() ? -1 : 1));
        } else {
          // descending
          tempDevices.sort((a, b) => (a.userName.toLowerCase() < b.userName.toLowerCase() ? 1 : -1));
        }
        break;

      case sortKeys.status:
        if (order === sortOrder.ascending) {
          tempDevices.sort((a, b) => (a.statusDT < b.statusDT ? -1 : 1));
        } else {
          // descending
          tempDevices.sort((a, b) => (a.statusDT < b.statusDT ? 1 : -1));
        }
        break;

      default:
        devLog('Invalid sort key:', key);
        doUpdate = false;
        break;
    }
    // }
    if (doUpdate) {
      setDevices(tempDevices);
    }
  };

  const handlerSortChanged = (id, key, order) => {
    //console.log("id:", id, "key:", key, "order:", order);
    if (id === key) {
      order = order === sortOrder.ascending ? sortOrder.descending : sortOrder.ascending;
    } else {
      key = id;
      order = sortOrder.ascending;
    }
    sortDevices(devices, accessLevel, key, order);
    setSelectedSortKey(key);
    setSelectedSortOrder(order);
  };
  async function fetchNotes(deviceUID) {
    const MAX_RETRIES = 3;
    let nextToken = null;
    let noteItems = [];
    let notesRecordID = 'UID_' + deviceUID;
    let retries = 0;

    devLog('Starting Fetch Devices Notes for device ', deviceUID, notesRecordID);
    // while(true) rather than do/while(nextToken != null): on a first-page
    // failure nextToken is still null, so a do/while would exit before the
    // catch could retry. Here a successful fetch breaks when there is no
    // continuation token; the catch retries the same page until MAX_RETRIES.
    while (true) {
      try {
        const eventFromAPI = await client.graphql({
          query: listDevices,

          variables: {
            recordType: RECORD_TYPES.NOTES,
            recordID: { beginsWith: notesRecordID },
            nextToken,
            limit: apiLimit,
          },
        });
        noteItems = noteItems.concat(eventFromAPI.data.listDevices.items);
        nextToken = eventFromAPI.data.listDevices.nextToken;
        retries = 0;
        if (nextToken == null) break;
      } catch (error) {
        retries++;
        devLog(`fetchNotes (retry ${retries}/${MAX_RETRIES}):`, error);
        if (retries >= MAX_RETRIES) {
          console.error('fetchNotes: giving up after max retries', error);
          break;
        }
        await timer(1000 * retries);
      }
    }

    devLog('Fetched Devices Notes', noteItems, 'for device ', deviceUID);
    // Probably don't need this as query now chagned to beginsWith.
    let ourNotes = noteItems.filter((e) => getIDSFromDb(e.recordID).longUID === deviceUID);
    devLog('ourNotes', ourNotes);
    setNotesData(ourNotes);
  }

  const updateDeviceLocalArchiveStatus = (deviceId, archived) => {
    setDevices((prev) => {
      const index = prev.findIndex((d) => d.uniqueDeviceId === deviceId);
      if (index === -1) {
        devLog('updateDeviceLocalArchiveStatus: ', deviceId, ' NOT found');
        return prev;
      }
      devLog('updateDeviceLocalArchiveStatus:', deviceId, '=', archived);
      const updated = prev.map((d, i) => (i === index ? { ...d, archived } : d));
      return sortDevicesArray(updated, selectedSortKeyRef.current, selectedSortOrderRef.current) ?? updated;
    });
  };

  const updateLocalDeviceName = (deviceId, name) => {
    setDevices((prev) => {
      const index = prev.findIndex((d) => d.uniqueDeviceId === deviceId);
      if (index === -1) {
        devLog('updateDeviceName: ', deviceId, ' NOT found');
        return prev;
      }
      devLog('updateDeviceName:', deviceId, '=', name);
      const updated = prev.map((d, i) => (i === index ? { ...d, userName: name } : d));
      return sortDevicesArray(updated, selectedSortKeyRef.current, selectedSortOrderRef.current) ?? updated;
    });
  };

  const updateLocalUserDeviceName = (deviceId, name) => {
    setDevices((prev) => {
      const index = prev.findIndex((d) => d.uniqueDeviceId === deviceId);
      if (index === -1) {
        devLog('updateUserDeviceName: ', deviceId, ' NOT found');
        return prev;
      }
      devLog('updateUserDeviceName:', deviceId, '=', name);
      const updated = prev.map((d, i) => (i === index ? { ...d, usersDeviceName: name } : d));
      return sortDevicesArray(updated, selectedSortKeyRef.current, selectedSortOrderRef.current) ?? updated;
    });
  };

  async function updateDeviceName(deviceId, name, type) {
    let record_ID = getRecordIDFromUID(deviceId);

    devLog('Info record update device name :', record_ID, RECORD_TYPES.INFO, name, type);
    try {
      const result = await client.graphql({
        query: updateDevice,

        variables: {
          input: {
            recordType: RECORD_TYPES.INFO,
            recordID: record_ID,

            userName: name,
            modelType: type,
          },
        },
      });
      const { data } = result;
      if (data.updateDevice.userName === name) {
        updateLocalDeviceName(deviceId, name);
      } else {
        devLog('edit: name NOT updated ->', data);
      }
    } catch (error) {
      devLog('updateDEvice Ne:', error);
      await timer(1000);
    }
  }

  async function updateUserDeviceName(deviceId, name, devType) {
    let record_ID = getRecordIDFromUID(deviceId);
    devLog('**** update User device name :', name, deviceId, record_ID);
    try {
      const getAccessResult = await fetchUserAccessRecord(loginId, deviceId);
      let result = null;
      if (getAccessResult !== null) {
        devLog('Updating User Device Name');
        result = await client.graphql({
          query: updateDevice,

          variables: {
            input: {
              recordType: RECORD_TYPES.ACCESS + loginId,
              recordID: record_ID,
              userName: name,
            },
          },
        });
        const { data } = result;

        if (data.updateDevice.userName === name || name === undefined) {
          updateLocalUserDeviceName(deviceId, name);
        } else {
          devLog('edit: user name NOT updated ->', data);
        }
      } else {
        devLog('Creating new record for User Device Name');
        result = await client.graphql({
          query: createDevice,

          variables: {
            input: {
              recordType: RECORD_TYPES.ACCESS + loginId,
              recordID: record_ID,
              userName: name,
            },
          },
        });
        const { data } = result;
        if (data.createDevice.userName === name || name === undefined) {
          updateLocalUserDeviceName(deviceId, name);
        } else {
          devLog('edit: user name NOT created ->', data);
        }
      }
    } catch (error) {
      devLog('Update  User Device Name Error:', error);
      await timer(250);
    }
  }

  async function updateUserDeviceHistory(deviceId) {
    let record_ID = getRecordIDFromUID(deviceId);
    // `name` was referenced here but is undefined in this function's scope
    // (not a param, no outer-scope binding) — would throw ReferenceError if
    // this ever executed. Dropped along with loginId (PII); this function is
    // dead code today (never called/exported), left as-is otherwise.
    devLog('**** update User device history:', deviceId, record_ID);
    try {
      const getUserResult = await fetchUserRecord(loginId);
      let result = null;
      if (getUserResult !== null) {
        devLog('Updating User Device Name');
        result = await client.graphql({
          query: updateDevice,

          variables: {
            input: {
              recordType: RECORD_TYPES.ACCESS + loginId,
              recordID: record_ID,
              userName: name,
            },
          },
        });
        const { data } = result;

        if (data.updateDevice.userName === name) {
          updateLocalUserDeviceName(deviceId, name);
        } else {
          devLog('edit: user name NOT updated ->', data);
        }
      } else {
        devLog('Creating new record for User Device Name');
        result = await client.graphql({
          query: createDevice,

          variables: {
            input: {
              recordType: RECORD_TYPES.ACCESS + loginId,
              recordID: record_ID,
              userName: name,
            },
          },
        });
      }
    } catch (error) {
      devLog('Update  User Device Name Error:', error);
      await timer(250);
    }
  }

  function isProductionTestInProductionNotCompleted(tempDevice) {
    var result = false;

    if (tempDevice.productionTest !== undefined && tempDevice.productionTest !== null) {
      if (tempDevice.productionTest !== 'Completed' && tempDevice.productionTest !== '') {
        result = true;
      }
    }
    return result;
  }

  const updateConnStatus = (tempDevice, theEvent) => {
    if (
      theEvent.updatedAt === undefined ||
      theEvent.updatedAt === null ||
      theEvent.updatedAt === 0 ||
      theEvent.updatedAt === '0'
    ) {
      let date = new Date();
      tempDevice.connStatDate = lightFormat(date, datetimeFormats.long);
    } else {
      let date = new Date(theEvent.updatedAt);
      try {
        tempDevice.connStatDate = lightFormat(date, datetimeFormats.long);
      } catch (error) {
        devLog('invalid time format :', theEvent);
      }
    }

    if (theEvent.connectionStatus === undefined || theEvent.connectionStatus === null) {
      tempDevice.connStatDesc = 'Undefined';
      //  console.log ("Connection Status Undefined", theEvent);
    } else {
      let connStat = theEvent.connectionStatus.toUpperCase();
      tempDevice.connStatDesc = connStat;
    }

    // if ( isProductionTestInProductionNotCompleted( tempDevice) )
    // {
    //   tempDevice.inProduction = true;
    // }

    if (tempDevice.connStatDesc === 'ONLINE') {
      tempDevice.connStatColor = COLOURS.good;
      tempDevice.connStatIcon = MdCheckCircleOutline;
    } else {
      tempDevice.connStatColor = COLOURS.warning;
      tempDevice.connStatIcon = MdSensorsOff;
    }
    return tempDevice;
  };

  // eslint-disable-next-line
  async function deleteEventRecord(record_ID) {
    const result = await client.graphql({
      query: deleteDevice,

      variables: {
        input: {
          recordType: RECORD_TYPES.EVENT,
          recordID: record_ID,
        },
      },
    });
    devLog('Deleting Event record', record_ID, result);
  }

  const isValidVersionNumber = (loopDevice) => {
    var versionNumberValid = true;
    if (
      loopDevice.versionNumber === null ||
      loopDevice.versionNumber === undefined ||
      loopDevice.versionNumber === 'undefined' ||
      loopDevice.versionNumber === 'Undefined' ||
      loopDevice.versionNumber === ''
    ) {
      versionNumberValid = false;
    }
    return versionNumberValid;
  };

  function doSetLoading(valueToSet) {
    setLoading(valueToSet);
    devLog('***** Set Loading ', valueToSet, '   *****');
  }

  function doSetLoadingLogs(valueToSet) {
    setLoadingLogs(valueToSet);
    devLog('***** Set Loading Logs', valueToSet, '   *****');
  }

  function doSetLoadingConfig(valueToSet) {
    setLoadingConfig(valueToSet);
    devLog('***** Set Loading Configuration', valueToSet, '   *****');
  }

  async function fetchDeviceAccess() {
    setGodMode(false);
    doSetLoading(true);
    setProgress(0);
    setInstallers([]);
    setInstalledBy([]);
    setAccessableDevices([]);

    let tmpGodMode = false;
    let DeviceAccess = [];
    let tmpDeviceAccess = [];
    let tmpGroupAccess = [];
    let tmpInstallers = [];
    const typeProgress = 16; // 100% / 5 type of queries

    devLog('started Fetching Access, loginId set:', !!loginId);

    // fetchDeviceAccess never calls doSetLoading(false) itself: loading only
    // clears once setAccessableDevices(...) below flips the [accessableDevices]
    // effect, which fires fetchDevices(), which eventually calls
    // doSetLoading(false) at the end of its own try/catch. The code below
    // must fall through to setAccessableDevices(...) rather than return
    // early, or loading gets stuck true forever.
    //
    // Promise.allSettled (not .all) so a permanent failure on one of these
    // doesn't discard the other's real data — .all is fail-fast and would
    // drop a successful INSTALLER fetch just because ACCESS rejected, or
    // vice versa.
    const [deviceAccessResult, installerResult] = await Promise.allSettled([
      fetchAllPages(listDevices, { recordType: RECORD_TYPES.ACCESS + loginId, limit: apiLimit }, 'listDevices'),
      fetchAllPages(listDevices, { recordType: RECORD_TYPES.INSTALLER, limit: apiLimit }, 'listDevices'),
    ]);
    if (deviceAccessResult.status === 'fulfilled') {
      DeviceAccess = DeviceAccess.concat(deviceAccessResult.value);
    } else {
      devLog('fetchDeviceAccess: fetching access records failed', deviceAccessResult.reason);
    }
    if (installerResult.status === 'fulfilled') {
      tmpInstallers = tmpInstallers.concat(installerResult.value);
    } else {
      devLog('fetchDeviceAccess: fetching installer records failed', installerResult.reason);
    }
    setProgress(typeProgress);

    devLog('Getting Access Levels', DeviceAccess);

    if (DeviceAccess.length) {
      DeviceAccess.forEach((item) => {
        if (getUIDFromDb(item.recordID) === '0000000000000000') {
          tmpGroupAccess.push(item.group);
          devLog('group: ', item.group);

          if (item.group === 'god_mode') {
            tmpGodMode = true;
          }
        } else {
          let UDeviceId = getUIDFromDb(item.recordID);
          let usersDeviceName = item.userName;
          var tempDeviceAccessDevice = {};

          if (usersDeviceName === null || usersDeviceName === undefined) {
            usersDeviceName = 'Undefined';
          } else {
            devLog('Users Device Name Set:', usersDeviceName, item, UDeviceId);
          }
          tempDeviceAccessDevice.usersDeviceName = usersDeviceName;
          tempDeviceAccessDevice.UID = UDeviceId;
          // console.log("device: ", UDeviceId);
          tmpDeviceAccess.push(tempDeviceAccessDevice);
        }
      });
    }

    if (tmpDeviceAccess.length === 0) {
      var tempDeviceAccessDevice = {};
      tempDeviceAccessDevice.usersDeviceName = 'Default Lobby Device';
      tempDeviceAccessDevice.UID = '10255320405691412';
      // console.log("device: ", UDeviceId);
      tmpDeviceAccess.push(tempDeviceAccessDevice);
    }

    setGodMode(tmpGodMode);
    setGroupAccess(tmpGroupAccess);
    setAccessableDevices(tmpDeviceAccess);
    // tmpDeviceAccess embeds user-provided device names — log a count, not the array.
    devLog('Device Access, count:', tmpDeviceAccess.length);
    devLog('Getting Installers...');

    if (tmpInstallers.length) {
      // Derive both subsets up front and set each state once, rather than
      // calling setInstallers/setInstalledBy per item inside the loop: each
      // of those calls read the same stale `installers`/`installedBy`
      // closure value, so multiple items of the same type in one pass would
      // all compute from that same stale snapshot and collapse down to
      // whichever call's computed value survived React's batching — losing
      // every earlier item, not just accumulating them.
      const newInstallers = tmpInstallers.filter((item) => item.recordID.startsWith('IID'));
      const newInstalledBy = tmpInstallers.filter((item) => item.recordID.startsWith('UID'));
      if (newInstallers.length) {
        setInstallers(newInstallers);
      }
      if (newInstalledBy.length) {
        setInstalledBy(newInstalledBy);
      }
    }
  }

  async function getInfoRecordForDevice(deviceUID) {
    try {
      const infoFromAPI = await client.graphql({
        query: listDevices,

        variables: {
          recordType: RECORD_TYPES.INFO,
          recordID: { beginsWith: infoID },
          nextToken,
          limit: apiLimit,
        },
      });
    } catch (error) {
      devLog('fetchInfo:', error);
      await timer(200);
    }
  }

  const blankEventElement = {
    recordID: '',
    recordType: RECORD_TYPES.EVENT,
    eventType: '',
    eventDesc: '',
    eventDate: '',
    eventTime: '',
    eventDateTime: '',
  };

  function setAccessLevels() {
    let accLvl = accessLevels.guest;
    if (!userGroups.includes('guest')) {
      if (userGroups.includes('Admin')) {
        accLvl = accessLevels.super;
        devLog('**** Set Access Level to Admin ****');
      } else if (userGroups.includes('Installer')) {
        accLvl = accessLevels.installer;
        devLog('**** Set Access Level to Installer ****');
      } else if (userGroups.includes('Service')) {
        accLvl = accessLevels.service;
        devLog('**** Set Access Level to Service ****');
      }
    } else {
      devLog('**** Set Access Level to Guest ****');
    }

    if (accLvl === accessLevels.guest) {
      // setLogParams(paramsGuest);
      // console.log("Guest")
      // setSelectedTab(tabItems.trends);
    }
    setAccessLevel(accLvl);
    return accLvl;
  }

  // Helper to fetch all pages for a given query/variables. Each page gets a
  // bounded number of attempts with a timer(200) backoff between them; once
  // exhausted, the error rethrows instead of silently returning partial data.
  const MAX_PAGE_ATTEMPTS = 3;
  async function fetchAllPages(query, variables, key = 'listInformation') {
    let items = [];
    let nextToken = null;
    do {
      let attempts = 0;
      let succeeded = false;
      while (!succeeded) {
        try {
          const response = await client.graphql({
            query,

            variables: { ...variables, nextToken },
          });
          // Parsing lives inside the try too: an unexpected response shape
          // (missing data/key) should get the same bounded retry as a
          // rejected request, not skip it and reject immediately.
          const data = response.data[key];
          items = items.concat(data.items);
          nextToken = data.nextToken;
          succeeded = true;
        } catch (error) {
          attempts++;
          if (attempts >= MAX_PAGE_ATTEMPTS) {
            throw error;
          }
          await timer(200);
        }
      }
    } while (nextToken != null);
    return items;
  }

  async function fetchDevices() {
    devLog('Getting groups', userGroups);
    let currAccessLevel = setAccessLevels();

    let buildInfoItems = [];
    let buildEventItems = [];
    let nextToken = null;
    const typeProgress = 20; // 100% / 5 type of queries
    const deltaMax = 4; // 1-4% per query

    setDevices([]);
    doSetLoading(true);
    setProgress(0);

    let currProgress = typeProgress;
    let tempProgress = 0;

    if (god_mode || currAccessLevel == accessLevels.service) {
      devLog('Getting Info Records');

      // const infoPromise = client.graphql({ query: listInformation, variables: {
      //         recordType: RECORD_TYPES.INFO,
      //         nextToken,
      //         limit: apiLimit
      //       }, });

      // const eventPromise = client.graphql({ query: listInformation, variables:  {
      //         recordType: RECORD_TYPES.EVENT,
      //         nextToken,
      //         limit: apiLimit
      //       },});

      try {
        const [allInfoItems, allEventItems] = await Promise.all([
          fetchAllPages(listInformation, { recordType: RECORD_TYPES.INFO, limit: apiLimit }),
          fetchAllPages(listInformation, { recordType: RECORD_TYPES.EVENT, limit: apiLimit }),
        ]);

        buildInfoItems = buildInfoItems.concat(allInfoItems);
        buildEventItems = buildEventItems.concat(allEventItems);
      } catch (error) {
        devLog('fetchDevices: fetching info/event records failed', error);
      }

      devLog('Got Info Records: ', buildInfoItems.length, 'Progress:', tempProgress, '/', currProgress);
      tempProgress = currProgress;
      currProgress += typeProgress;

      devLog('Getting Event Records');
      devLog('Got Event Records: ', buildEventItems.length, 'Progress:', tempProgress, '/', currProgress);
    } else // regular users and Installer-role users (per-device fetch) — god_mode/service are handled above
    {
      try {
        const perDeviceResults = await Promise.all(
          accessableDevices.map(async (device) => {
            let infoItem = null;
            let eventItems = [];
            // Each device is isolated in its own try/catch (mirroring the old
            // per-iteration behavior): getRecordIDFromUID/getShortIDFromUID are
            // included here so a malformed UID only drops this device, instead
            // of rejecting the whole Promise.all and aborting every device.
            try {
              let deviceUID = device.UID;
              let recordID = getRecordIDFromUID(deviceUID).toString();
              let shortID = getShortIDFromUID(deviceUID).toString();

              const infoIFromAPI = await client.graphql({
                query: getDevice,

                variables: {
                  recordType: RECORD_TYPES.INFO,
                  recordID: recordID,
                  nextToken,
                  limit: apiLimit,
                },
              });

              // console.log( "info FROM API", infoIFromAPI)
              if (infoIFromAPI.data.getDevice === null) {
                //Record not found... Try the short ID?
                let infoID = 'UID_' + shortID;
                devLog('infoID:', infoID);
                const infoIFromAPI2 = await client.graphql({
                  query: listInformation,

                  variables: {
                    recordType: RECORD_TYPES.INFO,
                    recordID: { beginsWith: infoID },
                    nextToken,
                    limit: apiLimit,
                  },
                });
                devLog('info FROM API2', infoIFromAPI2);

                if (infoIFromAPI2.data.listInformation.items.length > 0) {
                  infoIFromAPI2.data.listInformation.items[0].recordID = recordID;
                  infoItem = infoIFromAPI2.data.listInformation.items[0];
                } else {
                  devLog('fetchInfo: no info record found (short ID fallback) for device', device);
                }
              } else {
                infoItem = infoIFromAPI.data.getDevice;
              }
              // {beginsWith: "UID_53161124983816"},
              let eventID = recordID;
              // console.log( "EvnetID:", eventID)
              const eventItemFromAPI = await client.graphql({
                query: listInformation,

                variables: {
                  recordType: RECORD_TYPES.EVENT,
                  recordID: { beginsWith: eventID },
                  nextToken,
                  limit: apiLimit,
                },
              });

              eventItems = eventItemFromAPI.data.listInformation.items;
            } catch (error) {
              devLog('fetchInfo:', device, error);
            }
            return { infoItem, eventItems };
          }),
        );

        buildInfoItems = buildInfoItems.concat(
          perDeviceResults.filter((r) => r.infoItem != null).map((r) => r.infoItem),
        );
        buildEventItems = buildEventItems.concat(perDeviceResults.flatMap((r) => r.eventItems));
      } catch (error) {
        devLog('fetchDevices: per-device info/event fetch failed', error);
      }
      devLog('info record:', buildInfoItems, buildEventItems);
      // )};
    }
    devLog("Adding ID's to info record:");
    const infoItemsWithUID = buildInfoItems.map((infItem) => {
      // A record with a malformed recordID (not a valid UID-based string —
      // reachable via the god_mode/service path above, which fetches every
      // INFO record in the whole pool with no per-record validation, unlike
      // the per-device path's own try/catch) must not abort this whole map
      // and take down every other device's info record with it.
      try {
        let IDs = getIDSFromDb(infItem.recordID);
        // infItem.shortDeviceId = IDs.shortUID;
        infItem.uniqueDeviceId = IDs.longUID;
      } catch (error) {
        devLog('fetchDevices: could not derive IDs from info record', infItem, error);
      }
      return infItem;
    });
    // Re-render with the new array
    buildInfoItems = infoItemsWithUID;
    tempProgress = currProgress;
    currProgress += typeProgress;
    setProgress(tempProgress);
    devLog("Adding ID's to event record:");

    const eventItemsWithUID = buildEventItems.map((evntItem) => {
      try {
        let IDs = getIDSFromDb(evntItem.recordID);
        // evntItem.shortDeviceId = IDs.shortUID;
        evntItem.uniqueDeviceId = IDs.longUID;
      } catch (error) {
        devLog('fetchDevices: could not derive IDs from event record', evntItem, error);
      }
      return evntItem;
    });
    // Re-render with the new array
    buildEventItems = eventItemsWithUID;

    devLog('Done IDS:', buildInfoItems.length, buildEventItems.length);

    tempProgress = currProgress;
    currProgress += typeProgress;
    setProgress(tempProgress);
    var allDevices = [];
    devLog('Started Processing Records');

    const recordErrors = {
      duplicatesFound: [],
      userNameNotFound: [],
      infoItemsNotFound: [],
      evntsNotFound: [],
      invalidDates: [],
      inProduction: [],
    };

    const stats = {
      devicesTotal: 0,

      devicesRegistered: 0,
      devicesNotRegistered: 0,
      devicesUnRegistered: 0,
      devicesinProduction: 0,

      devicesStatusGood: 0,
      devicesStatusFault: 0,
      devicesStatusOffline: 0,
      devicesStatusUnknown: 0,

      totalFaults: 0,
      faults: [],
      // modes: [],
      versions: [],
      modelType: [],
      createdMonth: [],
      createdMonthTotals: [],
      PooltekDevices: 0,
      runningTotals: [],
      // modeByLocation: []
    };

    FIRMWARE_VERSIONS.forEach((element) => {
      stats.versions.push({ name: element.name, value: 0, colour: element.colour });
    });

    stats.versions.push({ name: 'NOT SET', value: 0, colour: COLOURS.unknown });

    // RECLAIM is kept in modelTypeDetails for completeness (see uid.js's
    // getModelTypeValueFromUID), but owner-confirmed 2026-07-16 it should
    // never appear in Stats, even via a real device that self-reports
    // modelType "Reclaim" through its own INFO record.
    const modelTypes = (APP_CONFIG.SWC_ENABLED ? modelTypeDetails : modelTypeDetails_no_SWC).filter(
      (element) => element.value !== controller_t.RECLAIM,
    );
    modelTypes.forEach((element) => {
      stats.modelType.push({ name: element.label, value: 0, colour: element.colour });
    });

    faultTable.forEach((element) => {
      stats.faults.push({ name: element.label, value: 0, colour: element.colour });
    });

    // if ( god_mode)
    // {
    //   console.log("******** GOD MODE ACTIVE ********");
    //   console.log("Creating info Records for non existant models");

    //   /* not needed as there are 2 currently add back in for testing.

    //   buildEventItems.forEach(element => {
    //     if (false === speedHasElementSID(buildInfoItems, element.shortDeviceId ) )
    //     {
    //       recordErrors.infoItemsNotFound.push(element);
    //     }
    //   });

    //   console.log("Info Records Filtering Finished", recordErrors.infoItemsNotFound.length);
    //   recordErrors.infoItemsNotFound.forEach((infoRecordNeeded) => {
    //     var tempInfoRecord = infoRecordNeeded;
    //     let UID = getUIDFromDb(infoRecordNeeded.recordID);
    //     tempInfoRecord.userName = "Info Not Found: "+UID;
    //     tempInfoRecord.modelType = "Unknown"
    //     tempInfoRecord.macAddr = getMacAddressFromUID(UID);
    //     console.log("Created info Records for:", tempInfoRecord);
    //     tempInfoRecord.unRegistered=true;
    //     buildInfoItems.push( tempInfoRecord);
    //   })
    //     */

    //   console.log("Done Info records");
    //   // const evntsNotFound = infoItems.filter((element1) => findShortUIDInListWithShortRecordID( getUIDFromDb( element1.recordID ) , eventItems ).length === 0) ;
    //   // buildInfoItems.forEach(element => {
    //   //   if (false === speedHasElementSID(buildEventItems, element.shortDeviceId ) )
    //   //   {
    //   //     recordErrors.evntsNotFound.push(element);
    //   //   }

    //   //   if(tempProgress < currProgress) {
    //   //     tempProgress += getRandomIntInclusive(1, deltaMax);
    //   //   }

    //   //   setProgress(tempProgress);
    //   // });

    //   //console.log("Event records not found", recordErrors.evntsNotFound.length);
    //   // console.log("Creating Event Records for non existant models");
    //   // // evntsNotFound.forEach ((thisDevice)=>
    //   // recordErrors.evntsNotFound.forEach ((thisDevice)=>
    //   // {
    //   //   var tempEvent = thisDevice;
    //   //   tempEvent.updatedAt = new Date();
    //   //   tempEvent.notRegistered = true;
    //   //   buildEventItems.push(tempEvent)
    //   // })
    //   console.log("Done Event Records for non existant models");
    // }

    var printed_records = 0;
    tempProgress = currProgress;
    setProgress(tempProgress);
    currProgress += 2 * typeProgress;
    devLog('Starting Processing all info items: ', buildInfoItems.length, 'Progress:', tempProgress, '/', currProgress);
    //circle round info items....

    let progress_itr = (2 * typeProgress) / buildInfoItems.length;
    var last_rounded = 10000;

    for (let infoItr = 0; infoItr < buildInfoItems.length; infoItr++) {
      let infoElement = buildInfoItems[infoItr];
      // let infoelementSI = getIDSFromDb(infoElement.recordID);
      if (god_mode || currAccessLevel == accessLevels.service) {
        if (false === speedHasElement(buildEventItems, infoElement.uniqueDeviceId)) {
          recordErrors.evntsNotFound.push(infoElement);

          var tempEvent = { ...blankEventElement }; // infoElement;
          tempEvent.recordID = infoElement.recordID;
          tempEvent.uniqueDeviceId = infoElement.uniqueDeviceId;
          tempEvent.updatedAt = new Date();
          tempEvent.notRegistered = true;
          buildEventItems.push(tempEvent);
        }
      }
      var tempDevice = {};
      // tempDevice.shortDeviceId = infoElement.shortDeviceId;
      tempDevice.uniqueDeviceId = infoElement.uniqueDeviceId;
      tempDevice.serialNumber = infoElement.serial_number;
      tempDevice.unRegistered = infoElement.unRegistered;
      tempDevice.createdAt = infoElement.createdAt;
      tempDevice.dateMade = infoElement.date;
      tempDevice.ioVersion = infoElement.ioVersion;
      tempDevice.exp1Version = infoElement.exp1Version;
      tempDevice.exp2Version = infoElement.exp2Version;
      tempDevice.chemVersion = infoElement.chemVersion;
      tempDevice.productionTest = infoElement.productionTest;
      tempDevice.inProduction = isProductionTestInProductionNotCompleted(tempDevice);
      const verData = FIRMWARE_VERSIONS.find((ver) => ver.name === infoElement.versionNumber);

      if (verData !== undefined) {
        tempDevice.verDesc = verData.name;
        tempDevice.verColor = verData.colour;
      }

      tempDevice.versionNumber = infoElement.versionNumber;
      tempDevice.currentFaults = infoElement.faults;

      if (isValidVersionNumber(tempDevice) === false) {
        tempDevice.versionNumber = 'NOT SET';
        tempDevice.verDesc = 'NOT SET';
        tempDevice.verColor = 'orange';
      }

      let isDeviceArchived = infoElement.archived === 'archived' ? true : false;
      tempDevice.archived = isDeviceArchived;
      if (isDeviceArchived) {
        devLog('device: ', tempDevice.uniqueDeviceId, ' is Archived', tempDevice.archived);
      }

      // let duplicatedItem = findUIDInListWithRecordID(tempDevice.uniqueDeviceId, buildInfoItems);
      // if ( duplicatedItem.length > 1)
      // {
      //   recordErrors.duplicatesFound.push( tempDevice)
      // }

      let theUserName = infoElement.userName;
      if (theUserName === null || theUserName === undefined) {
        theUserName = 'undefined';
        tempDevice.macAddress = getMacAddressFromUID(tempDevice.uniqueDeviceId);
        recordErrors.userNameNotFound.push(tempDevice);
      }
      tempDevice.userName = theUserName;

      if (
        infoElement.modelType === undefined ||
        infoElement.modelType === null ||
        infoElement.modelType === 'Unknown'
      ) {
        try {
          tempDevice.modelType = getModelTypeValueFromUID(infoElement.uniqueDeviceId).string;
        } catch (error) {
          tempDevice.modelType = 'Unknown';
          devLog(' tempDevice.modelType failed ', infoElement.recordID);
        }
      } else {
        tempDevice.modelType = infoElement.modelType;
      }

      if (tempDevice.modelType === 'Unknown') {
        devLog('tempDevice.modelType Unknown ', infoElement.recordID);
      }

      let currentUniqueID = tempDevice.uniqueDeviceId;
      let evtItm = {};
      // console.log(" tempDevice:", tempDevice);
      if (currentUniqueID === undefined) {
        devLog(' Undefined ID ', tempDevice);
      } else {
        evtItm = buildEventItems.filter((beItem) => beItem.uniqueDeviceId === currentUniqueID);

        if (evtItm === undefined || evtItm.length === 0) {
          devLog('event Item not found for ', currentUniqueID);
        } else {
          let theEvent = evtItm[0];
          if (tempDevice.uniqueDeviceId === undefined) {
            devLog('tempDevice.uniqueDeviceId === undefined');
          } else {
            let currentUID = tempDevice.uniqueDeviceId;

            evtItm.forEach((thisEvent) => {
              if (getUIDFromDb(thisEvent.recordID) === currentUID) {
                theEvent = thisEvent;
                // console.log("found the real device", item, theEvent.connectionStatus);
              }
            });

            tempDevice = updateConnStatus(tempDevice, theEvent);
            tempDevice.notRegistered = theEvent.notRegistered;

            if (isCurrentFaultsARealFault(tempDevice)) {
              tempDevice.ctrlStatDesc = 'FAULT';
              tempDevice.ctrlStatDate = tempDevice.connStatDate;
            } else {
              tempDevice.ctrlStatDate = tempDevice.connStatDate;
              tempDevice.ctrlStatDesc = 'GOOD';
            }

            // statusDT: a sortable timestamp for the device's active status
            // (OFFLINE / FAULT / GOOD). All three derive their date from the
            // same connection event (updatedAt, which also drives the displayed
            // connStatDate/ctrlStatDate), so use that as a raw epoch the
            // sortKeys.status comparator can order — connStatDate is a formatted
            // display string and does not sort chronologically.
            // Treat updatedAt the same way updateConnStatus does: undefined,
            // null, 0 and the string "0" all mean "no timestamp" (note "0" is
            // truthy, so a plain ternary would wrongly parse it). Guard NaN too
            // (an unparsable date) so the comparator stays stable; 0 sorts such
            // devices consistently to one end.
            const updatedAt = theEvent.updatedAt;
            const hasValidUpdatedAt =
              updatedAt !== undefined && updatedAt !== null && updatedAt !== 0 && updatedAt !== '0';
            const statusTimestamp = hasValidUpdatedAt ? new Date(updatedAt).getTime() : 0;
            tempDevice.statusDT = Number.isNaN(statusTimestamp) ? 0 : statusTimestamp;

            if (tempDevice.ctrlStatDesc === 'GOOD') {
              tempDevice.ctrlStatColor = COLOURS.good;
              tempDevice.ctrlStatIcon = MdCheckCircleOutline;
            } else {
              tempDevice.ctrlStatColor = COLOURS.error;
              tempDevice.ctrlStatIcon = MdErrorOutline;
            }

            var acccess_allowed = false;
            const devicesAccess = canIaccessController(tempDevice, accessableDevices, accessableGroups);

            if (god_mode || currAccessLevel == accessLevels.service) {
              acccess_allowed = true;
            } else {
              if (devicesAccess.canIaccessController === true) {
                acccess_allowed = true;
              }
            }

            if (acccess_allowed) {
              if (devicesAccess.item !== null) {
                tempDevice.usersDeviceName = devicesAccess.item.usersDeviceName;
                // console.log( "device Access:", devicesAccess, " tempDevice", tempDevice)
              }

              if (APP_CONFIG.ALLOW_PRODUCTION_UNITS) {
                allDevices.push(tempDevice);

                if (!tempDevice.inProduction) {
                  stats.devicesTotal++;
                } else {
                  recordErrors.inProduction.push(tempDevice);
                }
              } else {
                if (!tempDevice.inProduction) {
                  allDevices.push(tempDevice);
                  stats.devicesTotal++;
                } else {
                  recordErrors.inProduction.push(tempDevice);
                }
              }

              let loopDevice = tempDevice;
              if (loopDevice.inProduction === true) {
                stats.devicesinProduction++;
              } else {
                if (loopDevice.notRegistered === true) {
                  stats.devicesNotRegistered++;
                } else if (loopDevice.unRegistered === true) {
                  stats.devicesUnRegistered++;
                } else {
                  stats.devicesRegistered++;

                  if (loopDevice.connStatDesc === 'OFFLINE') {
                    stats.devicesStatusOffline++;
                  } else {
                    if (loopDevice.ctrlStatDesc === 'FAULT') {
                      stats.devicesStatusFault++;
                      if (isCurrentFaultsARealFault(loopDevice)) {
                        for (let index = 0; index < 16; index++) {
                          const bit_field = 1 << index;

                          if ((loopDevice.currentFaults & bit_field) === bit_field) {
                            const faultItem = getFaultTableItem(bit_field);
                            if (null != faultItem) {
                              let faultStat = stats.faults.find((itm) => itm.name === faultItem.label);
                              if (faultStat === undefined) {
                                // stats.faults.push({ name: loopDevice.verDesc, value: 1, colour: COLOURS.other});
                              } else {
                                faultStat.value++;
                              }
                            }
                          }
                        }
                      }
                    } else if (loopDevice.ctrlStatDesc === 'GOOD') {
                      stats.devicesStatusGood++;
                    } else {
                      if (printed_records < 10) {
                        devLog('unknown Status:', loopDevice.ctrlStatDesc, ' : ', loopDevice);
                        printed_records++;
                      }

                      stats.devicesStatusUnknown++;
                    }
                  }

                  let verStat = stats.versions.find((itm) => itm.name === loopDevice.verDesc);
                  if (verStat === undefined) {
                    if (isValidVersionNumber(loopDevice)) {
                      let verStat2 = stats.versions.find((itm) => itm.name === loopDevice.versionNumber);
                      if (verStat2 === undefined) {
                        //Get the build version from the loop device
                        let buildNumber = ExtractString(loopDevice.versionNumber, 'B');
                        if (parseInt(buildNumber, 10) >= 60) {
                          let betaStat = stats.versions.find((itm) => itm.name === 'Beta');
                          betaStat.value++;
                          devLog('Beta Version found', loopDevice.versionNumber);
                        } else {
                          stats.versions.push({ name: loopDevice.versionNumber, value: 1, colour: COLOURS.other });
                          devLog('Version', loopDevice.versionNumber, 'Added for:', loopDevice.uniqueDeviceId);
                        }
                      } else {
                        verStat2.value++;
                      }
                    } else {
                      if (loopDevice.verDesc === undefined) {
                        let undef = stats.versions.find((itm) => itm.name === 'NOT SET');
                        if (undef !== undefined) {
                          undef.value++;
                        }
                        devLog('Version Undefined for:', loopDevice.verDesc, loopDevice);
                      } else {
                        stats.versions.push({ name: loopDevice.verDesc, value: 1, colour: COLOURS.other });
                        devLog('Version', loopDevice.verDesc, 'Added for:', loopDevice.uniqueDeviceId);
                      }
                    }
                  } else {
                    verStat.value++;
                  }
                }

                let lowerCaseModelType = loopDevice.modelType.toLowerCase();
                // Owner-confirmed 2026-07-16: Reclaim must never show in Stats,
                // even though at least one real device self-reports this modelType.
                const isReclaim = lowerCaseModelType === 'reclaim';

                if (lowerCaseModelType.startsWith('pool')) {
                  stats.PooltekDevices++;
                  //console.log("Pool Device:", loopDevice.uniqueDeviceId, loopDevice.modelType)
                }

                if (!isReclaim) {
                  let modelStat = stats.modelType.find((itm) => itm.name.toLowerCase() === lowerCaseModelType);
                  if (modelStat === undefined) {
                    stats.modelType.push({ name: loopDevice.modelType, value: 1, colour: COLOURS.other });
                  } else {
                    modelStat.value++;
                  }
                }

                const creatdMonth = getMonthYearFromDate(loopDevice.createdAt);

                if (creatdMonth.label === 'Undefined' || creatdMonth.label === 'Jan-1900') {
                  // console.log( "Stats month Data not valid:", creatdMonth.label , "for", loopDevice.uniqueDeviceId )
                  recordErrors.invalidDates.push(loopDevice);
                } else {
                  let createdMonth = stats.createdMonth.find((itm) => itm.name === creatdMonth.label);
                  if (createdMonth === undefined) {
                    const newMonth = {
                      name: creatdMonth.label,
                      year: creatdMonth.Year,
                      month: creatdMonth.Month,
                    };
                    const modelTypes = (APP_CONFIG.SWC_ENABLED ? modelTypeDetails : modelTypeDetails_no_SWC).filter(
                      (element) => element.value !== controller_t.RECLAIM,
                    );
                    modelTypes.forEach((element) => {
                      newMonth[element.label.toLowerCase()] = 0;
                    });
                    newMonth['Total'] = 1;
                    if (!isReclaim) {
                      // loopDevice.modelType may be a self-reported string that isn't one
                      // of modelTypeDetails[_no_SWC]'s known labels (e.g. "Unknown", which
                      // isn't in that list) — initialize the key before incrementing so it
                      // doesn't silently go NaN. Checked via hasOwnProperty rather than
                      // `=== undefined`: modelKey is external data, and a value matching an
                      // inherited Object.prototype member name (e.g. "constructor",
                      // "toString", "__proto__") is never undefined via plain lookup, which
                      // would hit the same NaN bug this fix is closing. Also skip a modelKey
                      // that collides with one of newMonth's own reserved metadata fields
                      // (name/year/month) — incrementing those would corrupt the bucket
                      // itself, e.g. turn its month label into NaN.
                      const modelKey = loopDevice.modelType.toLowerCase();
                      if (!RESERVED_CREATED_MONTH_KEYS.includes(modelKey)) {
                        if (!Object.prototype.hasOwnProperty.call(newMonth, modelKey)) {
                          newMonth[modelKey] = 0;
                        }
                        newMonth[modelKey]++;
                      }
                    }
                    stats.createdMonth.push(newMonth);
                  } else {
                    if (!isReclaim) {
                      const modelKey = loopDevice.modelType.toLowerCase();
                      if (!RESERVED_CREATED_MONTH_KEYS.includes(modelKey)) {
                        if (!Object.prototype.hasOwnProperty.call(createdMonth, modelKey)) {
                          createdMonth[modelKey] = 0;
                        }
                        createdMonth[modelKey]++;
                      }
                    }
                    createdMonth['Total']++;
                  }
                }
              }
            }
          }
        }
      }

      if (tempProgress < currProgress) {
        tempProgress += progress_itr; //getRandomIntInclusive(1, deltaMax);
        let rounded = Math.round(tempProgress);
        if (rounded != last_rounded) {
          last_rounded = rounded;
          setProgress(rounded);
          // console.log("infoItr",infoItr, "Progress:", tempProgress, "/", currProgress ,rounded);
          await pause();
        }
      }
      setDevicesStats(stats);
    }
    devLog('Finished Processing all info items: Filtering Out Faults and Models with 0 records');

    stats.faults.forEach((faultFound) => {
      if (faultFound.value === 0) {
        stats.faults = stats.faults.filter((item) => item.name !== faultFound.name);
        devLog('Faults:', faultFound.name, 'removed');
      }
    });

    stats.modelType.forEach((modelFound) => {
      if (modelFound.value === 0) {
        stats.modelType = stats.modelType.filter((item) => item.name !== modelFound.name);
        devLog('Models:', modelFound.name, 'removed');
      }
    });

    devLog('Versions:', stats.versions);
    devLog('Ended Processing Records');

    if (
      recordErrors.duplicatesFound.length !== 0 ||
      recordErrors.userNameNotFound.length !== 0 ||
      recordErrors.inProduction.length !== 0 ||
      recordErrors.invalidDates.length !== 0
    ) {
      devLog('Record Errors: ');
      devLog('Duplicates found for', recordErrors.duplicatesFound);
      devLog('In Production', recordErrors.inProduction);
      devLog('Username not found for: ', recordErrors.userNameNotFound);
      devLog('Stats month Data not valid for: ', recordErrors.invalidDates);
    }

    stats.createdMonth.sort(function (a, b) {
      if (a.year === b.year) {
        return a.month < b.month ? -1 : 1;
      } else {
        return a.year < b.year ? -1 : 1;
      }
    });

    var runningSum = 0;

    var runningTotals = [];

    // const runningTotals = {
    //   name : creatdMonth.label,
    //   year : creatdMonth.Year,
    //   month : creatdMonth.Month,
    // };

    modelTypes.forEach((element) => {
      runningTotals[element.label.toLowerCase()] = 0;
    });

    stats.createdMonth.forEach((monthData) => {
      runningSum += monthData['Total'];
      monthData['Running Total'] = runningSum;

      devLog(monthData);

      const newMonth = {
        name: monthData.name,
        year: monthData.year,
        month: monthData.month,
      };

      runningTotals.name = monthData.name;
      runningTotals.year = monthData.year;
      runningTotals.month = monthData.month;

      modelTypes.forEach((element) => {
        runningTotals[element.label.toLowerCase()] += monthData[element.label.toLowerCase()];
        newMonth[element.label.toLowerCase()] = runningTotals[element.label.toLowerCase()];
      });
      stats.runningTotals.push(newMonth);
    });

    devLog('Created Month', stats.createdMonth);
    devLog('Pooltek Devices:', stats.PooltekDevices);
    devLog('Model Types:', stats.modelType);
    // allFaults.sort();
    // setLatestFaults(allFaults);
    // sortDevicesArray returns a sorted copy; since Phase 0.2 it no longer
    // mutates allDevices in place, so the sorted result must be used here.
    // Previously this called sortDevices() and then setDevices(allDevices),
    // which re-stored the unsorted array and dropped the default name sort.
    const sortedDevices = sortDevicesArray(allDevices, selectedSortKey, selectedSortOrder) ?? allDevices;
    // console.log("Alldevice :", sortedDevices.length, sortedDevices);
    setDevices(sortedDevices);

    doSetLoading(false);
  }

  const build_up_log_record_id = (deviceId, dateRange) => {
    const datDt0 = startOfDay(dateRange[0]);
    const datDt1 = endOfDay(dateRange[1]);
    var tm0 = '';
    var tm1 = '';
    try {
      tm0 = formatISO9075(datDt0).replace(' ', 'T');
      tm1 = formatISO9075(datDt1).replace(' ', 'T');
    } catch (error) {
      devLog('build_up_log_record_id:', error, deviceId, dateRange, datDt0, datDt1);
    }

    const totalPerDay = 300;
    const progressMax = totalPerDay * differenceInDays(datDt1, datDt0);
    let logRecordID = getRecordIDFromUIDNoDT(deviceId);
    let logRecordID_Start = logRecordID + '_DT_' + tm0;
    let logRecordID_End = logRecordID + '_DT_' + tm1;

    devLog('build_up_log_record_id: record Id: ', logRecordID, ' From: ', logRecordID_Start, ' To: ', logRecordID_End);

    return {
      id: logRecordID,
      start: logRecordID_Start,
      end: logRecordID_End,
      maxProgress: progressMax,
      startDate: datDt0,
      endDate: datDt1,
    };
  };

  async function fetchSetPointData(deviceId, dateRange) {
    setConfigData([]);
    doSetLoadingConfig(true);
    setProgress(0);
    setPlottableConfigParams([]);
    devLog('**** Fetch SETPoint Config Data Starting');
    let dateRangeData = build_up_log_record_id(deviceId, dateRange);
    let nextToken = null;
    let allItems = [];
    const typeProgress = 20; // 100% / 5 type of queries
    const deltaMax = 4; // 1-4% per query

    let currProgress = typeProgress;
    let tempProgress = 0;
    do {
      try {
        const apiData = await client.graphql({
          query: listDevices,

          variables: {
            recordType: RECORD_TYPES.CONFIG,
            recordID: {
              between: [dateRangeData.start, dateRangeData.end],
            },
            nextToken,
            limit: apiLimit,
          },
        });
        allItems = allItems.concat(apiData.data.listDevices.items);
        nextToken = apiData.data.listDevices.nextToken;
        if (tempProgress < currProgress) {
          tempProgress += getRandomIntInclusive(1, deltaMax);
        }
        setProgress(tempProgress);
      } catch (error) {
        devLog('listDevices COnfig Data error:', error);
        await timer(500);
      }
    } while (nextToken != null);
    devLog('Fetch COnfig Data: record Id: ', allItems);
    let tmpSetPointData = [];

    // Which config params the picker offers is fixed by the param map, so build the
    // list once here instead of growing it while walking records. Two reasons:
    //
    //  - it used to be seeded with POOL_SP alone and grown from whatever came back,
    //    so a date range with no CONFIG records offered one set-point while a range
    //    with records offered eight;
    //  - SWC-aware, matching the log builder. paramsConfig_SWC deliberately carries
    //    none of these (all eight are commented out in it), so an SWC device is no
    //    longer offered set-points from the non-SWC map that it does not report.
    //
    // The record walk below still reads paramsConfig, so no plotted value changes.
    const configDevice = devices.find((e) => e.uniqueDeviceId === deviceId);
    const configParamMap = configDevice !== undefined && is_a_SWC(configDevice) ? paramsConfig_SWC : paramsConfig;
    let tmpConfigParams = configParamMap.filter((p) => p.config && p.selectable);
    devLog('Fetch COnfig Data Progress:', tempProgress, '/', currProgress);
    tempProgress = currProgress;
    currProgress += typeProgress;

    for (let allItemsItr = 0, n = allItems.length; allItemsItr < n; allItemsItr++) {
      let tmpItm = allItems[allItemsItr];
      if (tmpItm !== null) {
        let date_time = getDateTimeFromRecordID(tmpItm.recordID);
        if (date_time === undefined) {
          devLog('skipping record with unparseable recordID', tmpItm.recordID);
          continue;
        }
        const datInRange =
          compareAsc(date_time, dateRangeData.startDate) >= 0 && compareAsc(date_time, dateRangeData.endDate) <= 0;
        let item = {};
        date_time.setSeconds(date_time.getSeconds() + 10);
        item.dateDisplay = lightFormat(date_time, datetimeFormats.short);
        item['Date/Time'] = lightFormat(date_time, datetimeFormats.long);
        item.dateTime = date_time;
        let hasData = false;
        let valinRange = true;
        let hasPhOrORPData = false;

        // Still paramsConfig, not the SWC-aware map above: this walk only extracts
        // values from the records, and narrowing it would change what gets plotted.
        paramsConfig.forEach((curr_param) => {
          if (curr_param.config) {
            const paramName = curr_param.name;
            const paramSearch = curr_param.search;

            let val = tmpItm[paramName];

            if (val !== null && paramSearch === true) {
              let itemDone = false;

              switch (paramName) {
                case paramsNames.SOLAR_SP:
                case paramsNames.SPA_2_SP:
                case paramsNames.POOL_2_SP:
                case paramsNames.SPA_SP:
                case paramsNames.POOL_SP: {
                  if (val >= 120) {
                    valinRange = false;
                    val = 0;
                  }
                  break;
                }
                case paramsNames.ORP_SP: {
                  hasPhOrORPData = true;

                  if (val == 2530) {
                    val = -1;
                  } else if (val == 2540) {
                    val = -2;
                  } else if (val == 2550) {
                    val = -3;
                  } else if (val >= 2500) {
                    val = 800;
                  }

                  break;
                }

                case paramsNames.PH_SP: {
                  hasPhOrORPData = true;
                  if (val == 25.3) {
                    val = -1;
                  } else if (val == 25.4) {
                    val = -2;
                  } else if (val == 25.5) {
                    val = -3;
                  } else if (val >= 25) {
                    val = 0;
                  }
                  // console.log("PH", val)
                  break;
                }
                case paramsNames.SPA_MODE: {
                  if (val != 0) {
                    val = 50; //todo - need to graph a hint.
                  }
                }
                default:
                  break;
              }
              // console.log("paramName", paramName)
              if (datInRange && valinRange) {
                hasData = true;
              }

              if (itemDone === false) {
                item[curr_param.text] = val;
              }
            }
          }
        });

        // Once per record, not once per param. This used to sit inside the forEach
        // above, so every iteration after hasData flipped true pushed the same
        // `item` again — one CONFIG record produced eight identical rows in
        // configData, and TrendsView concatenates and sorts without deduping.
        // Matches the log builder, which already pushes after its forEach closes.
        if (hasData) {
          tmpSetPointData.push(item);
        }
      }
    }
    // console.log("appliances:", tmpAppliancesConfigured);
    setPlottableConfigParams(tmpConfigParams);
    setConfigData(tmpSetPointData);
    doSetLoadingConfig(false);
    devLog('Fetch SetPoint Config Data Ending');
  }

  // The loading flag is cleared here, in a finally, so that it is cleared on EVERY exit path of
  // the fetch below — returning normally or throwing. Follow-up G left the clear unguarded so a
  // stale fetch would clear it; H then guarded it, which fixed the premature clear but removed
  // that accidental safety net: if the newest fetch threw before its tail, nothing cleared the
  // flag at all and the spinner stuck. PR review caught it. This gets both — only the newest
  // request clears, and it always does.
  //
  // Split into a wrapper rather than wrapping the body in place because runLogFetch is ~590
  // lines; a try/finally around it would have re-indented the lot for two lines of behaviour,
  // burying this change in whitespace. The token is claimed here so the finally can check it.
  async function fetchLogData(deviceId, dateRange) {
    const fetchToken = ++logFetchTokenRef.current;
    try {
      await runLogFetch(deviceId, dateRange, fetchToken);
    } finally {
      // Only the newest request clears. An older one finishing later must leave the flag alone,
      // or it takes the spinner away while the current fetch is still paging.
      if (logFetchTokenRef.current === fetchToken) {
        doSetLoadingLogs(false);
      }
    }
  }

  // fetchToken is claimed by fetchLogData above and passed in; checked at the tail — see the
  // selection block at the end.
  async function runLogFetch(deviceId, dateRange, fetchToken) {
    setLogData([]);
    doSetLoadingLogs(true);
    setProgress(0);
    // setPumpData([])
    setPumpTrackerData([]);
    setApplianceTrackerData([]);

    // setApplianceData([])

    devLog('Fetch Log Data Starting for device:', deviceId);
    let dateRangeData = build_up_log_record_id(deviceId, dateRange);
    let nextToken = null;
    let allItems = [];
    phSelectionRef.current = false;
    ORPSelectionRef.current = false;
    // Accumulated per fetch and assigned to the refs in the guarded tail, rather than written
    // straight to them mid-loop. The loop only ever set them true, so a stale fetch landing
    // last used to resurrect "this controller has pH/ORP readings" for a controller that has
    // none — the same clobbering as the data writes, just in a ref instead of state.
    let hasPhReadings = false;
    let hasORPReadings = false;
    const typeProgress = 20; // 100% / 5 type of queries
    const deltaMax = 4; // 1-4% per query

    let currProgress = typeProgress;
    let tempProgress = 0;
    do {
      try {
        const apiData = await client.graphql({
          query: listDevices,

          variables: {
            recordType: RECORD_TYPES.DATA,
            recordID: {
              between: [dateRangeData.start, dateRangeData.end],
            },
            nextToken,
            limit: apiLimit,
          },
        });
        allItems = allItems.concat(apiData.data.listDevices.items);
        nextToken = apiData.data.listDevices.nextToken;
        if (tempProgress < currProgress) {
          tempProgress += getRandomIntInclusive(1, deltaMax);
        }
        setProgress(tempProgress);
      } catch (error) {
        devLog('listDevices logData error:', error);
        await timer(500);
      }
    } while (nextToken != null);
    // console.log("Fetch Log Data: record Id: ", allItems);
    let tmpLogData = [];
    let tmpPumpData = [];
    let tmpApplianceData = [];
    let tmpParams = [].concat(paramsConfig[0]);
    let tmpAppliancesConfigured = [];
    let dataSetPHRT = [];
    let dataSetORPRT = [];
    let dataSetPH = [];
    let dataSetORP = [];
    let dataSetLabelsPHORP = [];
    let APPLIANCE_ON_VALUE = 9;
    devLog('Progress:', tempProgress, '/', currProgress);
    tempProgress = currProgress;
    currProgress += typeProgress;

    for (let allItemsItr = 0, n = allItems.length; allItemsItr < n; allItemsItr++) {
      let tmpItm = allItems[allItemsItr];
      if (tmpItm !== null) {
        let date_time = getDateTimeFromRecordID(tmpItm.recordID);
        if (date_time === undefined) {
          devLog('skipping record with unparseable recordID', tmpItm.recordID);
          continue;
        }
        const datInRange =
          compareAsc(date_time, dateRangeData.startDate) >= 0 && compareAsc(date_time, dateRangeData.endDate) <= 0;
        let item = {};
        item.dateDisplay = lightFormat(date_time, datetimeFormats.short);
        item['Date/Time'] = lightFormat(date_time, datetimeFormats.long);
        item.dateTime = date_time;
        let pump_item = {};
        let hasData = false;
        let valinRange = true;
        let hasPumpData = false;

        let hasPhOrORPData = false;
        let ORP_data = null;
        let PH_data = null;
        let ORP_RT_data = null;
        let PH_RT_data = null;

        var curr_param_config = paramsConfig;

        const checkingdevice = devices.find((e) => e.uniqueDeviceId === deviceId);

        if (checkingdevice !== undefined) {
          if (is_a_SWC(checkingdevice)) {
            curr_param_config = paramsConfig_SWC;
            devLog('Using SWC Params');
          } else {
            devLog('Using Default Params not an SWC, device:', checkingdevice.modelType);
          }
        }

        curr_param_config.forEach((curr_param) => {
          const paramName = curr_param.name;
          const paramSearch = curr_param.search;
          const paramSelectable = curr_param.selectable;

          // Params flagged `config` are offered by fetchSetPointData's list, not this
          // one. Offering them here as well is what put eight of them in the picker
          // twice — the Pool/Spa/Pool 2/Spa 2/Solar/ORP/pH set-points and SPA Mode:
          // each builder deduped only its own list, and TrendsView concatenates the
          // two. Values are still read below either way, so this changes what is
          // *offered*, never what can be plotted.
          const offerInPicker = paramSelectable && !curr_param.config;

          let val = tmpItm[paramName];
          // Computed here because the switch below reassigns val. The enclosing branch tests
          // `val !== null`, and `undefined !== null` is true, so a record that simply does not
          // carry this field still falls through into the switch. That laxity is left alone —
          // it also decides `hasData` and what goes into `item`, so tightening it would move
          // plotted data for every param — and only the pH/ORP flags, which claim a probe
          // exists, are gated on a value actually being present.
          const hasValue = val !== null && val !== undefined;

          if (val !== null && paramSearch === true) {
            let itemDone = false;

            switch (paramName) {
              case paramsNames.ROOF: {
                // hasPoolRoofOrWaterData = true;
                if (val >= 120) {
                  valinRange = false;
                  val = 0;
                }
                // Roof = val;
                break;
              }
              case paramsNames.WATER: {
                // hasPoolRoofOrWaterData = true;
                if (val >= 120) {
                  valinRange = false;
                  val = 0;
                }
                // Water = val;
                break;
              }
              case paramsNames.POOL: {
                // hasPoolRoofOrWaterData = true;
                if (val >= 120) {
                  valinRange = false;
                  val = 0;
                }
                // Pool = val;
                break;
              }
              case paramsNames.APP1:
              case paramsNames.APP2:
              case paramsNames.APP3:
              case paramsNames.APP4:
              case paramsNames.APP5:
              case paramsNames.APP6:
              case paramsNames.APP7:
              case paramsNames.APP8:
              case paramsNames.APP9:
              case paramsNames.APP10:
              case paramsNames.APP11:
              case paramsNames.APP12:
              case paramsNames.APP13:
              case paramsNames.APP14: {
                var applianceItem = {};
                var applianceConfigured = false;
                let appliancestring = lightFormat(date_time, datetimeFormats.short);
                // Capture the raw 0/1/2 (Off/On/Not Configured) before the
                // switch below overwrites `val` for the chart's Y-value —
                // applianceStatecolours is ordered gray/green/yellow to
                // match this raw value directly (0=gray, 1=green, 2=yellow).
                var appliance_colour_idx = val;

                switch (val) {
                  case 0:
                    appliancestring += ' OFF';
                    applianceConfigured = true;
                    val = 0;
                    break;
                  case 1:
                    appliancestring += ' ON';
                    applianceConfigured = true;
                    val = APPLIANCE_ON_VALUE;
                    break;
                  case 2:
                    appliancestring += ' Not In Use ';
                    val = -1;
                    break;
                  default:
                    appliancestring += '???' + val;
                    val = -1;
                    appliance_colour_idx = 0;
                    break;
                }
                applianceItem.id = curr_param.id;
                applianceItem.DisplayString = appliancestring;
                applianceItem.name = curr_param.text;
                applianceItem.colour = applianceStatecolours[appliance_colour_idx];
                applianceItem.val = val;
                applianceItem.value = val;
                applianceItem.dateDisplay = item.dateDisplay;
                applianceItem.dateTime = item.dateTime;
                tmpApplianceData.push(applianceItem);

                if (applianceConfigured) {
                  if (!tmpAppliancesConfigured.find((e) => e === curr_param.id)) {
                    tmpAppliancesConfigured.push(curr_param.id);
                  }
                }
                break;
              }
              case paramsNames.ACID:
              case paramsNames.CHLORINE: {
                var dosingItem = {};
                var dosingConfigured = false;
                let DOSING_VALUE = 9;
                let appliancestring = lightFormat(date_time, datetimeFormats.short);
                // Same fix as the appliance case above: capture the raw
                // 0/1/2 (Idle/Dosing/Not Configured) before `val` gets
                // overwritten for the chart's Y-value, instead of indexing
                // applianceStatecolours with the overwritten value (9 or -1,
                // both out of bounds -> undefined).
                let dosing_colour_idx = val;
                switch (val) {
                  case 0:
                    appliancestring += ' IDLE';
                    dosingConfigured = true;
                    break;
                  case 1:
                    appliancestring += ' DOSING';
                    dosingConfigured = true;
                    val = DOSING_VALUE;
                    break;
                  case 2:
                    appliancestring += ' Not In Use ';
                    val = -1;
                    break;
                  default:
                    appliancestring += '???' + val;
                    val = -1;
                    dosing_colour_idx = 0;
                    break;
                }
                dosingItem.id = curr_param.id;
                dosingItem.DisplayString = appliancestring;
                dosingItem.name = curr_param.text;
                dosingItem.colour = applianceStatecolours[dosing_colour_idx];
                dosingItem.value = val;
                dosingItem.val = val;
                dosingItem.dateDisplay = item.dateDisplay;
                dosingItem.dateTime = item.dateTime;
                tmpApplianceData.push(dosingItem);

                // console.log( "Acid Chlorine State",j, paramName ,val , item, tmpItm, dosingItem)
                if (dosingConfigured) {
                  if (!tmpAppliancesConfigured.find((e) => e === curr_param.id)) {
                    tmpAppliancesConfigured.push(curr_param.id);
                  }
                }
                break;
              }
              case paramsNames.PUMP: {
                let pump_speed = (val & 0xff) + 1;
                let pump_state = val >> 8;
                let pump_string = lightFormat(date_time, datetimeFormats.short);
                let dateString = pump_string;
                let pump_value = 0;
                let ON_VALUE = 0.5;
                switch (pump_state) {
                  case 0:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //'STARTUP',
                  case 1:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'IDLE',
                  case 2:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'POWER P',
                  case 3:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'POWERED',
                  case 4:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'WAIT',
                  case 5:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = pump_speed + 2;
                    break; //  'PRIME',
                  case 6:
                    pump_string += ' ' + pumpStateStrings[pump_state] + '@ Speed:' + pump_speed;
                    pump_value = pump_speed;
                    break; //  'SET SPEED',
                  case 7:
                    pump_string += ' ' + pumpStateStrings[pump_state] + '@ Speed:' + pump_speed;
                    pump_value = pump_speed;
                    break; //  'SPD POLL',
                  case 8:
                    pump_string += ' ' + pumpStateStrings[pump_state] + '@ Speed:' + pump_speed;
                    pump_value = pump_speed;
                    break; //  'CHK SPD',
                  case 9:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = ON_VALUE + pump_speed;
                    break; //  'SET ON',
                  case 10:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = ON_VALUE + pump_speed;
                    break; //  'CHK ON P',
                  case 11:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = ON_VALUE + pump_speed;
                    break; //  'CHK ON',
                  case 12:
                    pump_string += ' ' + pumpStateStrings[pump_state] + '@ Speed:' + pump_speed;
                    pump_value = ON_VALUE + pump_speed;
                    break; //  'RUNNING',
                  case 13:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = ON_VALUE;
                    break; //  'RUN ON',
                  case 14:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'PWR DWN',
                  case 15:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'V OFF P',
                  case 16:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'VER OFF',
                  case 17:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    pump_value = -ON_VALUE;
                    break; //  'PUMP FAULT',
                  case 18:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'PRIME OFF',
                  case 19:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'RUN ON',
                  case 20:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'POWER OFF',
                  case 21:
                    pump_string += ' ' + pumpStateStrings[pump_state];
                    break; //  'POWER ON'
                  default:
                    pump_string += ' UnKnown';
                }

                pump_item.id = 21;
                pump_item.DisplayString = pump_string;
                pump_item.PumpState = pumpStateStrings[pump_state];
                pump_item.PumpSpeed = pump_speed;
                pump_item.colour = pumpStatecolours[pump_state];
                pump_item.name = 'pump';
                pump_item.value = pump_value;
                pump_item.dateString = dateString;
                pump_item.dateTime = date_time;
                // console.log( "pump State" , pump_state , "pump Speed", pump_speed , "Item: ", pump_item)
                itemDone = true;
                hasPumpData = true;
                break;
              }
              case paramsNames.ORP: {
                hasPhOrORPData = true;
                // console.log("ORP Reading taken:",val, paramsNames.ORP)
                if (val == 2530) {
                  val = -1;
                } else if (val == 2540) {
                  val = -2;
                } else if (val == 2550) {
                  val = -3;
                } else if (val >= 2500) {
                  val = 800;
                }
                ORP_data = val;
                break;
              }

              case paramsNames.PH: {
                hasPhOrORPData = true;
                if (val == 25.3) {
                  val = -1;
                } else if (val == 25.4) {
                  val = -2;
                } else if (val == 25.5) {
                  val = -3;
                } else if (val >= 25) {
                  val = 0;
                }
                PH_data = val;
                // console.log("PH", val)
                break;
              }
              case paramsNames.PHRT: {
                hasPhOrORPData = true;
                if (val >= 100) {
                  val = val / 100;
                }
                if (val == 25.3) {
                  val = -1;
                } else if (val == 25.4) {
                  val = -2;
                } else if (val == 25.5) {
                  val = -3;
                } else if (val >= 25) {
                  val = 0;
                }
                PH_RT_data = val;
                break;
              }

              case paramsNames.ORPRT: {
                hasPhOrORPData = true;
                if (val == 2530) //NOt yet Valid
                {
                  val = -1;
                } else if (val == 2540) {
                  val = -2;
                } else if (val == 2540) {
                  val = -3;
                } else if (val >= 2500) {
                  val = 800;
                }
                ORP_RT_data = val;
                break;
              }

              case paramsNames.OTHER: {
                if (val >= 120) {
                  valinRange = false;
                  val = 0;
                }

                break;
              }
              case paramsNames.AMPS:
              case paramsNames.VOLTS:
              case paramsNames.VOLTS_2:
                valinRange = true;
                break;
              default:
                break;
            }
            // console.log("paramName", paramName)

            if (datInRange && valinRange) {
              hasData = true;

              // hasValue, not the enclosing `val !== null`: these two decide whether TrendsView
              // offers a pH/ORP chart at all, so an absent field must not claim a probe.
              if (hasValue) {
                if (paramName === paramsNames.PH || paramName === paramsNames.PHRT) {
                  hasPhReadings = true;
                }
                if (paramName === paramsNames.ORP) {
                  // console.log("ORP Reading taken:",val)
                  hasORPReadings = true;
                }

                if (paramName === paramsNames.ORPRT) {
                  hasORPReadings = true;
                }
              }
            }

            if (itemDone === false) {
              item[curr_param.text] = val;
            }

            if (offerInPicker) {
              if (!tmpParams.find((e) => e.name === paramName)) {
                tmpParams.push(curr_param);
              }
            }
          } else {
            if (offerInPicker) {
              if (!tmpParams.find((e) => e.name === paramName)) {
                tmpParams.push(curr_param);
              }
            }
          }
        });
        if (hasData) {
          // console.log("item:", item)
          tmpLogData.push(item);
        }
        if (hasPhOrORPData) {
          dataSetLabelsPHORP.push(item.dateDisplay);
          if (ORP_data != null) {
            dataSetORP.push(ORP_data);
          }

          if (PH_data != null) {
            dataSetPH.push(PH_data);
          }

          if (ORP_RT_data != null) {
            dataSetORPRT.push(ORP_RT_data);
          }

          if (PH_RT_data != null) {
            dataSetPHRT.push(PH_RT_data);
          }
        }
        if (hasPumpData) {
          tmpPumpData.push(pump_item);
        }
      }
    }

    // Everything this fetch computed is published together, and only if this is still the
    // newest request. Two fetchLogData calls overlap whenever a controller is switched or the
    // date range is changed twice quickly, with no ordering guarantee — so without this an
    // older request finishing last left a previous controller's, or a previous range's, data on
    // the Trends and Appliances tabs. #128 fixed only the picker selection and said so.
    //
    if (logFetchTokenRef.current === fetchToken) {
      // console.log("appliances:", tmpAppliancesConfigured);
      setConfiguredAppliance(tmpAppliancesConfigured);
      // setPumpData(tmpPumpData);
      setPumpTrackerData(getPumpTrackerdata(tmpPumpData));
      setLogDevice(deviceId);
      setLogData(tmpLogData);
      setPlottableParams(tmpParams);
      setORPPHLabels(dataSetLabelsPHORP);
      setORPData(dataSetORP);
      setPHData(dataSetPH);
      setORPRTData(dataSetORPRT);
      setPHRTData(dataSetPHRT);
      // Assigned, not OR-ed with the current value: these were reset to false at the start of
      // this fetch, so the two are equivalent today — assignment just states the intent that
      // this fetch's findings replace, rather than accumulate onto, whatever was there.
      phSelectionRef.current = hasPhReadings;
      ORPSelectionRef.current = hasORPReadings;
      // Highest real appliance/dosing id: APP1-APP14 are ids 1-14, ACID/CHLORINE
      // (the dosing case) are 15/16 — see the `case paramsNames.APP1:` .. `CHLORINE:`
      // list above, which this must be kept in sync with by hand if a new
      // appliance/dosing id is ever added. Index 0 is unused padding (no
      // appliance carries id 0), so the array below covers ids 0..MAX inclusive.
      const MAX_APPLIANCE_TRACKER_ID = 16;
      const temptrackerdata = Array.from({ length: MAX_APPLIANCE_TRACKER_ID + 1 }, (_, itr) =>
        getTrackerdataForAppliances_with_id(tmpApplianceData, itr),
      );
      setApplianceTrackerData(temptrackerdata);
      // Carry the picker selection over only when this is a refetch of the SAME controller,
      // which is what a date-range change is. The old rule was "keep it if the new param list
      // still contains this text", and appliance params are positional — every non-SWC
      // controller offers all of "Appliance 1".."Appliance 14" — so it always matched.
      // Selections therefore survived a controller switch and silently pointed at whatever
      // that slot happens to be on the next device, including slots reading "Not Set".
      // Nothing reset them either: only resetOnSignOut ever did.
      const sameControllerAsSelection = deviceId === selectionDeviceRef.current;
      selectionDeviceRef.current = deviceId;

      let tmpSelected = [paramsConfig[0].text];
      if (sameControllerAsSelection && selectedParams.length !== 0) {
        for (let itr = 0; itr < tmpParams.length; itr++) {
          if (selectedParams.includes(tmpParams[itr].text) && !tmpSelected.includes(tmpParams[itr].text)) {
            tmpSelected.push(tmpParams[itr].text);
          }
        }
      }
      setSelectedParams(tmpSelected);
      devLog('*** Selected Params:', tmpSelected, tmpParams, '***');
    }

    // The loading flag is NOT cleared here — fetchLogData's finally owns it, so that a throw
    // anywhere above still clears it. The paging try/catch only covers the page request itself;
    // everything from the record loop down is unprotected, which is how the stuck spinner
    // happened.
    devLog('*** Fetch Log Data Ending');
  }

  async function fetchFaultsData(deviceId, dateRange) {
    doSetLoading(true);
    // setHistoryFaults([]);
    let tmpHistFlts = [];
    let nextToken = null;
    let dateRangeData = build_up_log_record_id(deviceId, dateRange);

    const typeProgress = 20; // 100% / 5 type of queries
    const deltaMax = 4; // 1-4% per query

    let currProgress = typeProgress;
    let tempProgress = 0;

    devLog(
      'Fetch Fault Data: record Id: ',
      dateRangeData.id,
      ' From: ',
      dateRangeData.start,
      ' To: ',
      dateRangeData.end,
    );
    do {
      try {
        const faultAPIData = await client.graphql({
          query: listDevices,

          variables: {
            recordType: RECORD_TYPES.FAULTS,
            recordID: {
              between: [dateRangeData.start, dateRangeData.end],
            },
            nextToken,
            limit: apiLimit,
          },
        });
        tmpHistFlts = tmpHistFlts.concat(faultAPIData.data.listDevices.items);
        nextToken = faultAPIData.data.listDevices.nextToken;
        if (tempProgress < currProgress) {
          tempProgress += getRandomIntInclusive(1, deltaMax);
        }
        setProgress(tempProgress);
        // console.log("List Faults good:",faultAPIData, tmpHistFlts, nextToken);
      } catch (error) {
        devLog('List Faults error:', error);
        await timer(500);
        //fNextToken = error;
      }
    } while (nextToken != null);

    // console.log("Fetch Fault Data Done: record Id: ", dateRangeData.id , " Faults: ",  tmpHistFlts );
    doSetLoading(false);
    // setLogFaults(tmpLogFaults);
    setHistoryFaults(tmpHistFlts);
  }

  function viewDevice(device) {
    devLog('View Device', device.uniqueDeviceId);
    setHistoryFaults([]);
    setNotesData([]);
    const resetDateRange = [startOfYesterday(), endOfToday()];
    setSelectedDateRange(resetDateRange);
    handleSelectedDevice(device);

    //needs to clear the data
    setCurrentDevice(devices.find((e) => e.uniqueDeviceId === device));
    fetchSetPointData(device, resetDateRange);
    fetchLogData(device, resetDateRange);
    fetchNotes(device);
  }

  function resetForBackNavigation() {
    setCurrentDevice(undefined);
    setSelectedDevice(undefined);
    setHistoryFaults([]);
    setNotesData([]);
  }

  function resetOnSignOut() {
    setDevices([]);
    setLogDevice('');
    setLogData([]);
    setAccessableDevices([]);
    setInstallers([]);
    setInstalledBy([]);
    setHistoryFaults([]);
    handleSelectedDevice('');
    setSelectedDevices([]);
    setSelectedDateRange([startOfYesterday(), endOfToday()]);
    setSelectedParams([paramsConfig[0].text]);
    selectionDeviceRef.current = '';
    setSelectedSortKey(sortKeys.name);
    setSelectedSortOrder(sortOrder.ascending);
    setDevicesStats({});
  }

  useEffect(() => {
    devLog('selected Device Changed - ', selectedDevice);
    setCurrentDevice(devices.find((e) => e.uniqueDeviceId === selectedDevice));
  }, [selectedDevice, devices]);

  useEffect(() => {
    if (accessableDevices.length !== 0) {
      devLog('accessableDevices changed. Fetching device details...');
      fetchDevices();
    } else {
      devLog('accessableDevices  Reset to None.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessableDevices]);

  useEffect(() => {
    devLog('god_mode  changed.');
  }, [god_mode]);

  useEffect(() => {
    if (loginId.length !== 0) {
      fetchDeviceAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginId]);

  return {
    devices,
    setDevices,
    loading,
    loadingLogs,
    loadingConfig,
    progress,
    logDevice,
    selectedDevices,
    selectedFilterDateRange,
    setSelectedFilterDateRange,
    selectedDateRange,
    selectedParams,
    setSelectedParams,
    historyFaults,
    notes_data,
    setNotesData,
    accessableGroups,
    accessableDevices,
    selectedDevice,
    selectedShortID,
    logData,
    applianceTrackerDataArray,
    pumpTrackerData,
    ORPPHLabelsData,
    ORPData,
    PHData,
    ORPRTData,
    PHRTData,
    god_mode,
    installers,
    installedBy,
    selectedSortKey,
    selectedSortOrder,
    currentDevice,
    devicesStats,
    configData,
    plottableParams,
    plottableConfigParams,
    phSelection: phSelectionRef.current,
    ORPSelection: ORPSelectionRef.current,
    configuredAppliances,
    isApplianceConfigured,
    getTrackerdataForAppliances_with_id,
    getPumpTrackerdata,
    handleSelectedDevice,
    refreshData,
    refreshFaultsData,
    isLoadingOrNoDevice: is_loading_or_no_device,
    refreshDataByDate,
    refreshFaultDataByDate,
    updateSelectedDevices,
    sortDevicesArray,
    sortDevices,
    handlerSortChanged,
    updateDeviceLocalArchiveStatus,
    updateLocalDeviceName,
    updateLocalUserDeviceName,
    updateDeviceName,
    updateUserDeviceName,
    fetchNotes,
    fetchDeviceAccess,
    fetchDevices,
    fetchSetPointData,
    fetchLogData,
    fetchFaultsData,
    viewDevice,
    resetForBackNavigation,
    resetOnSignOut,
  };
}
